
import { mapGetters } from 'vuex';
import { messageLinkRecipient, messageArchive, messageUnarchive, messageUnlinkRecipient, messageAssign, messageUnassign } from '@/api/message';
import LinkRecipientDialog from '@/views/_messages/components/LinkRecipientDialog.vue';
import { Risolto_Channel_Enum } from '@/generated/graphql';
import { GET_ACTION } from '@/gql';
import axios from 'axios';
import gql from 'graphql-tag';
import { attachmentDownload } from '@/api/attachment';
import type { Attachment } from '@/schema/attachment';
import SendNewEmail from '@/views/_messages/components/SendNewEmail.vue';
import { defineComponent } from 'vue';

interface EmailMetadata {
  email: {
    to: string;
    cc?: string | string[];
    bcc?: string | string[];
    body: {
      html: string;
      text?: string;
    };
  };
}

interface MessageRecipient {
  reference: string;
  recipient_by_reference: {
    reference: string;
    legal_entity: string;
    account_id: string;
  };
}

interface MessageAssignee {
  person_by_reference: {
    reference: string;
    username: string;
  };
}

interface MessageAttachment {
  reference: string;
  attachment_by_reference: {
    reference: string;
    metadata: {
      key: string;
      name: string;
      bucket: string;
      scheme: string;
      content_type: string;
    };
  };
}

interface Message {
  reference: string;
  from_: string;
  subject: string;
  metadata: EmailMetadata;
  archived_at: string | null;
  received_at: string;
  message_recipients: MessageRecipient[] | null;
  message_assignees: MessageAssignee[] | null;
  message_attachments: MessageAttachment[] | null;
}

const get_message = gql`
query get_message(
	$participant: uuid!
	$message: uuid!
) {
	message: risolto_message(
		where: {
			participant: { _eq: $participant }
			reference: { _eq: $message }
		}
	) {
		reference
		from_
		subject
		metadata
		archived_at
		received_at
		message_recipients {
			recipient_by_reference {
				reference
				legal_entity
				account_id
			}
		}
		message_assignees {
			person_by_reference {
				reference
				username
			}
		}
		message_attachments {
			reference
			attachment_by_reference {
				reference
				metadata
			}
		}
	}
}
`;

export default defineComponent({
	name: 'EmailDetailDialog',
	components: {
		LinkRecipientDialog,
		SendNewEmail
	},
	props: {
		visible: { required: true, type: Boolean },
		messageRef: { type: String, default: null }
	},
	data() {
		return {
			sendAccountOverviewActionEmailLoading: false,
			assignLoading: false,
			message: null as Message | null,
			showUnlinkedReplyPopup: false,
			toolbarHeight: 0
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore', 'participantUsers']),
		dialogTitle() {
			return this.message?.subject || 'email detail';
		},
		modalContentStyle() {
			return `padding-top: ${this.toolbarHeight}px`;
		}
	},
	watch: {
		messageRef: {
			immediate: true,
			async handler() {
				await this.getMessage();
			}
		},
		'$store.state.apollo.risolto_message': function() {
			this.getMessage();
		}
	},
	mounted() {
		const toolbarEl = this.$refs.toolbar;
		if (toolbarEl) {
			this.toolbarHeight = toolbarEl.offsetHeight;
		}
	},
	methods: {
		async getMessage() {
			if (!this.$store.getters.selectedParticipantStore || !this.messageRef) {
				return;
			}

			try {
				const result = await this.$apollo.query({
					query: get_message,
					variables: {
						participant: this.$store.getters.selectedParticipantStore,
						message: this.messageRef
					}
				});

				if (result && result.data && result.data.message) {
					this.message = result.data.message[0];
				}
			} catch (error) {
				console.error('Error fetching message:', error);
			}
		},

		handleClose() {
			this.$store.commit('risolto/SET_EMAIL_DETAIL_DIALOG_VISIBLE', false);
		},

		async handleLink() {
			const selection = await this.$refs.linkRecipientDialog.show();
			if (!Array.isArray(selection) || selection.length === 0) {
				return;
			}

			await messageLinkRecipient(
				this.$store.state.risolto.selectedParticipantStore,
				null,
				[this.messageRef],
				selection
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleUnlink() {
			if (!this.message || !this.message.message_recipients || this.message.message_recipients.length === 0) return;

			await messageUnlinkRecipient(
				this.$store.state.risolto.selectedParticipantStore,
				null,
				[this.messageRef],
				this.message.message_recipients.map((r) => r.recipient_by_reference.reference)
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleArchive() {
			await messageArchive(
				this.$store.state.risolto.loggedInUser.reference,
				this.$store.state.risolto.selectedParticipantStore,
				[this.messageRef]
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleUnarchive() {
			await messageUnarchive(
				this.$store.state.risolto.selectedParticipantStore,
				[this.messageRef]
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleNewMessage() {
			this.sendAccountOverviewActionEmailLoading = true;
			try {
				await this.handleSendAccountOverview();
			} finally {
				this.sendAccountOverviewActionEmailLoading = false;
			}
		},

		async handleSendAccountOverview() {
			if (!this.message || !this.message.message_recipients || this.message.message_recipients.length === 0) return;

			const payload = {
				participant: this.$store.state.risolto.selectedParticipantStore,
				legal_entity: this.message.message_recipients[0].recipient_by_reference.legal_entity,
				recipient: this.message.message_recipients[0].recipient_by_reference.reference,
				channel: Risolto_Channel_Enum.Email,
				message: this.message.reference
			};

			const response = await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/mailbox/create-message-reply`, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: response.data.action
				}
			});
			const action = result.data.action[0];

			this.$store.commit('actions/UPSERT_ACTION', action);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
			this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);

			this.handleClose();
		},

		extractEmails(input: string | string[] | undefined): string {
			if (!input) {
				return '';
			}

			// Convert array input to a single string
			if (Array.isArray(input)) {
				input = input.join(', ');
			}

			// Ensure input is a string
			if (typeof input !== 'string') {
				return '';
			}

			// Updated regex pattern for emails
			const regex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
			const matches: string[] = [];
			let match: RegExpExecArray | null;

			while ((match = regex.exec(input)) !== null) {
				matches.push(match[0].toLowerCase()); // match[0] contains the full email
			}

			return matches.join(', ');
		},

		async downloadAttachment(attachment: Attachment) {
			attachmentDownload(this.selectedParticipantStore, attachment);
		},

		formatDate(dateString: string | null): string {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleString(undefined, {
				year: 'numeric',
				month: 'numeric',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		},

		toggleUnlinkedReplyPopup() {
			this.showUnlinkedReplyPopup = !this.showUnlinkedReplyPopup;
		},

		async handleAssign(assigneeReference: string) {
			if (!this.messageRef) return;

			this.assignLoading = true;
			try {
				await messageAssign(
					this.selectedParticipantStore,
					[this.messageRef],
					assigneeReference
				);

				this.$store.commit('apollo/RISOLTO_MESSAGE');
				await this.getMessage();

				this.$message({
					message: this.$t('messages.assignee_updated'),
					type: 'success'
				});
			} catch (error) {
				console.error('Error assigning message:', error);
				this.$message({
					message: this.$t('somethingWentWrong'),
					type: 'error'
				});
			} finally {
				this.assignLoading = false;
			}
		},

		async handleUnassign(assigneeReference: string) {
			if (!this.messageRef) return;

			try {
				await messageUnassign(
					this.selectedParticipantStore,
					this.messageRef,
					assigneeReference
				);

				this.$store.commit('apollo/RISOLTO_MESSAGE');
				await this.getMessage();

				this.$message({
					message: this.$t('messages.assignee_updated'),
					type: 'success'
				});
			} catch (error) {
				console.error('Error unassigning message:', error);
				this.$message({
					message: this.$t('somethingWentWrong'),
					type: 'error'
				});
			}
		},

		navigateToRecipient(recipientReference: string) {
			if (!recipientReference) return;

			// Navigate to recipient detail page
			this.$router.push({
				name: 'recipient-detail',
				params: {
					id: recipientReference,
					participant: this.selectedParticipantStore
				}
			});

			// Close the dialog
			this.handleClose();
		}
	}
});
